import React, { useEffect, useState, ChangeEvent, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
//import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BUTTONS, TITLES } from 'locales/locales';
import CustomButton from 'components/button/CustomButton';
//import CustomText from 'components/text/CustomText';
import Loading from 'components/spinner/Loading';
import { useCheckMfaMutation, useLoginMutation } from 'app/services/auth.service';
import { useAppSelector } from 'app/hooks';


interface IMfaProps {
  email: string
  password: string
  clientId: string
  captcha:string
  secret: any
  onSuccess: (kvkkRequired: boolean) => void;
}

const Mfa = ({ email, password, clientId, secret,captcha:captchaInput,onSuccess }:IMfaProps) => {

  const { t } = useTranslation();
  // const navigate = useNavigate();
  const [checkMfa] = useCheckMfaMutation();
  const [login] = useLoginMutation()
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [resend, setResend] = useState<boolean>(false)
  const [newSecret, setNewSecret] = useState<any>(secret)
  const [timer, setTimer] = useState(0);
  

  const systemSettings = useAppSelector((state) => state.system.data)
  
  useEffect(() => {
    const firstInput = document.getElementById('mfa-input-0') as HTMLInputElement;
    firstInput?.focus();
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value.length === 1 && index < 5) {
        const nextInput = document.getElementById(`mfa-input-${index + 1}`) as HTMLInputElement;
        nextInput?.focus();
      }
    }
  };

const handlePaste = (e: any) => {
  const paste = e.clipboardData.getData('Text');

  if (/^\d{6}$/.test(paste)) {
    const newCode = paste.split('');
    setCode(newCode);

    newCode.forEach((digit:any, i:number) => {
      const input = document.getElementById(`mfa-input-${i}`) as HTMLInputElement;
      if (input) {
        input.value = digit;
      }
    });

    const lastInput = document.getElementById(`mfa-input-${5}`) as HTMLInputElement;
    lastInput?.focus();
    e.preventDefault(); 
  }
};

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      const previousInput = document.getElementById(`mfa-input-${index - 1}`) as HTMLInputElement;
      previousInput?.focus();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const mfaCode = code.join('')
    if (mfaCode.length < 6) {
      return
    }
    try {
      Loading.ref.show()
      const result: any = await checkMfa({ secret: newSecret, mfaToken: mfaCode })
      if (result.data.success) {
        onSuccess(result)
      }
    } catch (error) {
      setResend(true)
      //Yanlış kod girildiğinde input alanını sıfırla.
      // setCode(Array(6).fill(''))
      console.error('MFA verification failed', error)
    } finally {
      Loading.ref.hide()
    }
  };

  const handleResendClick = async () => {
    try {
      const loginParams = {
        email,
        password,
        clientId,
      }
      if(systemSettings?.features?.captchaEnabled){
        (loginParams as any).captcha = captchaInput.trim();
      }
      const mfa: any = await login(loginParams)
      if (mfa?.data?.success) {
        setNewSecret(mfa?.data?.secret)
        // setResend(false)
        setTimer(59); 
      }
    } catch (error) {
      // setResend(true)
      console.error('MFA resend failed', error)
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0 && interval) {
      clearInterval(interval);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timer]);

  return (
    <MFAContainer>
      <MFAForm onSubmit={handleSubmit}>
        <TextContainer>
          <Title>{t(TITLES.MFA_TITLE)}</Title>
          <Description>{t(TITLES.MFA_TITLE_DETAIL)}</Description>
        </TextContainer>
        <MFAInputs>
          {code.map((value, index) => (
            <MFAInput
              key={index}
              id={`mfa-input-${index}`}
              type="text"
              value={value}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              maxLength={1}
              autoComplete="off"
              required={resend ? false : true}
            />
          ))}
        </MFAInputs>
        <CustomButton
          typography="header1"
          size="large"
          text={t(BUTTONS.SIGN_IN) as string}
          disabled={code.join('').length < 6}
          type={'primary'}
        />
        <br/>
        {resend && (
          <CustomButton
            typography="header1"
            size="large"
            text={timer > 0 ? `${timer}` : (t(BUTTONS.RESEND_MFA_CODE) as string)}
            disabled={timer > 0}
            type={'danger'}
            onClick={handleResendClick}
          />
        )}
      </MFAForm>
    </MFAContainer>
  )
};

export default Mfa;

const MFAContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f0f2f5;
  width:100%;
`;

const MFAForm = styled.form`
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  width: 475px;
`;


const MFAInputs = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const MFAInput = styled.input`
  width: 3rem;
  height: 3rem;
  font-size: 2rem;
  text-align: center;
  border: 2px solid #ccc;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: rgb(19, 159, 219);
    box-shadow: 0 0 3px rgba(0, 123, 255, 0.5);
  }
`;

// const StyledCustomText = styled(CustomText)`
//   white-space: pre-line;
//   margin-bottom: 40px;
//   font-size: 17px;
// `;

const TextContainer = styled.div`
  padding: 20px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
`;
